import { User, Customer, Booking, Communication } from '../types';

/**
 * Combined-app API service.
 *
 * The UI and API are now served from the same origin, so we use a relative
 * path. In Vite dev mode this is proxied to http://localhost:3001 by
 * vite.config.ts. In production (Azure App Service) the Node server serves
 * both the SPA and the API on the same host/port.
 *
 * If you ever need to point the UI at a different host (e.g. for a staging
 * build) set VITE_API_BASE_URL at build time.
 */
const API_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/bookings/api';

class ApiService {
  private getHeaders() {
    const token = localStorage.getItem('ottosbarn_token');
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
  }

  private async handleResponse(response: Response) {
    if (!response.ok) {
      // 401/403 — token expired or invalid. Notify the app so it can clear
      // the session and bounce the user to the login screen.
      if (response.status === 401 || response.status === 403) {
        window.dispatchEvent(
          new CustomEvent('auth:unauthorized', {
            detail: { status: response.status },
          })
        );
      }
      const errorData = await response.json().catch(() => ({}));
      const err = new Error(
        errorData.message ||
          errorData.error ||
          `API Error: ${response.status} ${response.statusText}`
      ) as Error & { status?: number };
      err.status = response.status;
      throw err;
    }
    if (response.status === 204) return null;
    return response.json();
  }

  // Auth
  async login(
    username: string,
    password: string
  ): Promise<{ user: User; token: string }> {
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    return this.handleResponse(response);
  }

  // Customers (includes joined bookings)
  async getCustomers(): Promise<Customer[]> {
    const response = await fetch(`${API_URL}/customers`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  async saveCustomer(customer: Partial<Customer>): Promise<Customer> {
    const response = await fetch(`${API_URL}/customers`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(customer),
    });
    return this.handleResponse(response);
  }

  async deleteCustomer(id: string): Promise<void> {
    const response = await fetch(`${API_URL}/customers/${id}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  // Bookings
  async saveBooking(booking: Booking): Promise<Booking> {
    const response = await fetch(`${API_URL}/bookings`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(booking),
    });
    return this.handleResponse(response);
  }

  async deleteBooking(id: string): Promise<void> {
    const response = await fetch(`${API_URL}/bookings/${id}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  // Health (used by the UI status indicator)
  async checkHealth(): Promise<boolean> {
    try {
      const response = await fetch(`${API_URL}/health`, {
        signal: AbortSignal.timeout(3000),
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  // Fetch all Gmail-sourced communications (live, last 12 months, in:anywhere)
  async getCommunications(): Promise<Communication[]> {
    const response = await fetch(`${API_URL}/communications`, {
      headers: this.getHeaders(),
    });
    return this.handleResponse(response);
  }

  // Communications for a single customer (uses the ?customerId= filter).
  async getCustomerCommunications(customerId: string): Promise<Communication[]> {
    const response = await fetch(
      `${API_URL}/communications?customerId=${encodeURIComponent(customerId)}`,
      { headers: this.getHeaders() }
    );
    return this.handleResponse(response);
  }
}

export const api = new ApiService();
